import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = unknown / loading, false = anonymous, object = authenticated
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("cryptpe_token");
    if (!token) {
      setUser(false);
      return;
    }
    const clearSession = () => {
      localStorage.removeItem("cryptpe_token");
      localStorage.removeItem("cryptpe_user");
      setUser(false);
    };
    const applyUser = (data) => {
      setUser(data);
      try { localStorage.setItem("cryptpe_user", JSON.stringify(data)); } catch (_) {}
    };
    try {
      const { data } = await api.get("/auth/me");
      applyUser(data);
    } catch (e) {
      const status = e?.response?.status;
      // Only a definitive auth rejection ends the session. Transient
      // failures (network blip, 5xx, gateway cold start) must NOT log
      // the user out on a page refresh.
      if (status === 401 || status === 403) return clearSession();
      try {
        await new Promise((r) => setTimeout(r, 1200));
        const { data } = await api.get("/auth/me");
        applyUser(data);
      } catch (e2) {
        const st2 = e2?.response?.status;
        if (st2 === 401 || st2 === 403) return clearSession();
        // Still unreachable — hydrate from the cached snapshot so the
        // user stays on the page they refreshed; token is kept.
        try {
          const cached = JSON.parse(localStorage.getItem("cryptpe_user"));
          if (cached && typeof cached === "object") return setUser(cached);
        } catch (_) {}
        setUser(false);
      }
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // 2FA challenge — caller decides what to do (Login page asks for
    // the second-factor code). Important: we do NOT touch localStorage
    // or set user state here, because the challenge token is short-
    // lived and isn't a real session.
    if (data.requires_2fa) {
      return { requires_2fa: true, challenge_token: data.challenge_token };
    }
    localStorage.setItem("cryptpe_token", data.token);
    try { localStorage.setItem("cryptpe_user", JSON.stringify(data.user)); } catch (_) {}
    setUser(data.user);
    return data.user;
  };

  const verify2FA = async (challenge_token, code) => {
    const { data } = await api.post("/auth/login/2fa-verify", {
      challenge_token, code,
    });
    localStorage.setItem("cryptpe_token", data.token);
    try { localStorage.setItem("cryptpe_user", JSON.stringify(data.user)); } catch (_) {}
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("cryptpe_token", data.token);
    try { localStorage.setItem("cryptpe_user", JSON.stringify(data.user)); } catch (_) {}
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("cryptpe_token");
    localStorage.removeItem("cryptpe_user");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, verify2FA, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
